import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Chip,
  Skeleton,
  Pagination,
  CircularProgress,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import BlockIcon from '@mui/icons-material/Block'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import RefreshIcon from '@mui/icons-material/Refresh'
import InfoIcon from '@mui/icons-material/Info'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotification } from '@/hooks/useNotification'
import { useChains, useChainId, usePublicClient } from 'wagmi'
import CreateSafeModal from '@/localsafe.eth/components/CreateSafeModal'
import TransferModal from './TransferModal'
import SafeDetailsModal from './SafeDetailsModal'
import { getEnv } from '@/config/env'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import OperationalSafeFactoryAbi from '@/abis/OperationalSafeFactory.json'
import SafeGuardAbi from '@/abis/SafeGuard.json'
import { createPublicClient, http, type Abi, getAddress } from 'viem'
import { mainnet, sepolia } from 'wagmi/chains'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import {
  encodeContractCall,
  buildSafeTransactionParams,
} from '@/utils/safeTransactionHelpers'
import { submitSafeTransactionToService } from '@/utils/safeTransactionService'
import { isUserRejectedError } from '@/utils/errorHandler'
import {
  formatHash,
  formatAddress,
  formatTokenBalance,
} from '@/utils/formatting'
import {
  SKELETON_WIDTH_SMALL,
  POLLING_INTERVAL_BALANCE,
  SAFES_PER_PAGE,
  SAFE_API_DEFAULT_LIMIT,
  POLLING_INTERVAL_PAUSED_STATUS,
  POLLING_INTERVAL_OPERATIONAL_WALLETS,
} from '@/constants/main'
import {
  createCardStyles,
  createTextFieldStyles,
  createSecondaryTextStyles,
} from '@/components/shared/commonStyles'
import SectionHeader from '@/components/shared/SectionHeader'
import { fetchSafes, updateSafeName } from '@/utils/safeApi'
import { useSafeStatus } from '@/hooks/useSafeStatus'
import { copyToClipboard } from '@/utils/clipboard'
import { useWallet } from '@/hooks/useWallet'

interface SafeAccount {
  id: string
  safeId: string
  address: string
  name: string
  chainId: string
}

export default function SafeAccounts() {
  const { showSuccess, showError } = useNotification()
  const { colors } = useTheme()
  const chains = useChains()
  const chainId = useChainId()
  const publicClient = usePublicClient()
  const { blockSafe, unblockSafe, deleteSafe, filterDeleted } = useSafeStatus()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState<string>('')
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<SafeAccount | null>(
    null
  )
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedDetailsAccount, setSelectedDetailsAccount] =
    useState<SafeAccount | null>(null)
  const [balances, setBalances] = useState<
    Record<string, { balance: string; isLoading: boolean }>
  >({})
  const [safes, setSafes] = useState<SafeAccount[]>([])
  const [isLoadingSafes, setIsLoadingSafes] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pausedAccounts, setPausedAccounts] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [isBlocking, setIsBlocking] = useState<string | null>(null)
  const [isUpdatingName, setIsUpdatingName] = useState<string | null>(null)
  const isFirstBalanceLoadRef = useRef<Set<string>>(new Set())

  const {
    stngProxy,
    mainnetRpcUrl,
    sepoliaRpcUrl,
    apiUrl,
    operationalSafeFactory,
    operationalSafeGuard,
    treasurySafe,
  } = useMemo(() => getEnv(), [])

  const {
    isReady: isTreasurySafeReady,
    buildSafeTransaction: buildTreasurySafeTransaction,
    signSafeTransaction: signTreasurySafeTransaction,
    executeSafeTransaction: executeTreasurySafeTransaction,
    getSafeTransactionHash: getTreasurySafeTransactionHash,
    isTransactionReadyToExecute: isTreasuryTransactionReadyToExecute,
  } = useSafe(treasurySafe as `0x${string}`)

  const { address: userAddress } = useWallet()

  const loadSafes = useCallback(async () => {
    if (!apiUrl || !chainId) {
      console.warn('API URL or chain ID not configured')
      return
    }

    setIsLoadingSafes(true)
    try {
      const response = await fetchSafes(apiUrl, {
        limit: SAFE_API_DEFAULT_LIMIT,
        offset: 0,
        order: 'DESC',
        chainIds: chainId,
      })

      const accountsList: SafeAccount[] = response.items.map((item) => ({
        id: `${item.chainId}-${item.address}`,
        safeId: item.id,
        address: item.address,
        name: item.name || '',
        chainId: String(item.chainId),
      }))

      const filteredAccounts = filterDeleted(accountsList)
      setSafes(filteredAccounts)
    } catch (error) {
      console.error('Failed to fetch safes:', error)
      showError('Failed to load Safe Accounts')
    } finally {
      setIsLoadingSafes(false)
    }
  }, [apiUrl, chainId, showError, filterDeleted])

  useEffect(() => {
    loadSafes()

    const interval = setInterval(() => {
      loadSafes()
    }, POLLING_INTERVAL_OPERATIONAL_WALLETS)

    return () => clearInterval(interval)
  }, [loadSafes])

  const accounts = useMemo(() => {
    const startIndex = (currentPage - 1) * SAFES_PER_PAGE
    const endIndex = startIndex + SAFES_PER_PAGE
    return safes.slice(startIndex, endIndex)
  }, [safes, currentPage])

  const accountsKey = useMemo(
    () => accounts.map((a) => a.id).join(','),
    [accounts]
  )

  const totalPages = Math.ceil(safes.length / SAFES_PER_PAGE)

  useEffect(() => {
    const checkPausedStatus = async () => {
      if (
        !operationalSafeGuard ||
        !publicClient ||
        !chainId ||
        accounts.length === 0
      ) {
        return
      }

      try {
        let rpcUrl: string | undefined
        let chain: typeof mainnet | typeof sepolia

        if (chainId === mainnet.id) {
          rpcUrl = mainnetRpcUrl || mainnet.rpcUrls.default.http[0]
          chain = mainnet
        } else if (chainId === sepolia.id) {
          rpcUrl = sepoliaRpcUrl || sepolia.rpcUrls.default.http[0]
          chain = sepolia
        } else {
          return
        }

        if (!rpcUrl) return

        const chainPublicClient = createPublicClient({
          chain,
          transport: http(rpcUrl),
        })

        const pausedChecks = accounts.map((account) => ({
          address: operationalSafeGuard as `0x${string}`,
          abi: SafeGuardAbi as Abi,
          functionName: 'isSafePaused' as const,
          args: [account.address as `0x${string}`],
        }))

        const results = await chainPublicClient.multicall({
          contracts: pausedChecks,
        })

        const pausedSet = new Set<string>()

        results.forEach((result, index) => {
          if (
            result.status === 'success' &&
            result.result !== undefined &&
            result.result !== null &&
            result.result === true
          ) {
            pausedSet.add(accounts[index].id)
          }
        })

        setPausedAccounts(pausedSet)
      } catch (error) {
        console.error('Failed to check paused status:', error)
      }
    }

    checkPausedStatus()

    const interval = setInterval(
      checkPausedStatus,
      POLLING_INTERVAL_PAUSED_STATUS
    )

    return () => {
      clearInterval(interval)
    }
  }, [
    operationalSafeGuard,
    publicClient,
    chainId,
    accounts,
    mainnetRpcUrl,
    sepoliaRpcUrl,
  ])

  useEffect(() => {
    if (accounts.length === 0 && currentPage > 1 && safes.length > 0) {
      setCurrentPage(currentPage - 1)
    }
  }, [accounts.length, currentPage, safes.length])

  useEffect(() => {
    if (!stngProxy || accounts.length === 0) {
      if (accounts.length === 0) {
        setBalances({})
        isFirstBalanceLoadRef.current.clear()
      }
      return
    }

    const currentAccountIds = new Set(accounts.map((a) => a.id))
    const trackedIds = Array.from(isFirstBalanceLoadRef.current)
    trackedIds.forEach((id) => {
      if (!currentAccountIds.has(id)) {
        isFirstBalanceLoadRef.current.delete(id)
      }
    })

    const fetchBalances = async () => {
      const setBalanceToError = (accountId: string) => {
        setBalances((prev) => {
          const currentBalance = prev[accountId]?.balance
          if (currentBalance === '—') {
            return prev
          }
          return {
            ...prev,
            [accountId]: { balance: '—', isLoading: false },
          }
        })
      }

      const accountsByChain = accounts.reduce((acc, account) => {
        const chainId = account.chainId
        if (!acc[chainId]) {
          acc[chainId] = []
        }
        acc[chainId].push(account)
        return acc
      }, {} as Record<string, typeof accounts>)

      for (const [chainIdStr, chainAccounts] of Object.entries(
        accountsByChain
      )) {
        const chainId = parseInt(chainIdStr)
        let rpcUrl: string | undefined
        let chain: typeof mainnet | typeof sepolia

        if (chainId === mainnet.id) {
          rpcUrl = mainnetRpcUrl || mainnet.rpcUrls.default.http[0]
          chain = mainnet
        } else if (chainId === sepolia.id) {
          rpcUrl = sepoliaRpcUrl || sepolia.rpcUrls.default.http[0]
          chain = sepolia
        } else {
          chainAccounts.forEach((account) => {
            setBalanceToError(account.id)
          })
          continue
        }

        if (!rpcUrl) {
          chainAccounts.forEach((account) => {
            setBalanceToError(account.id)
          })
          continue
        }

        chainAccounts.forEach((account) => {
          const isFirstLoad = !isFirstBalanceLoadRef.current.has(account.id)
          if (isFirstLoad) {
            isFirstBalanceLoadRef.current.add(account.id)
            setBalances((prev) => {
              const existingBalance = prev[account.id]?.balance
              if (existingBalance && existingBalance !== '—') {
                return prev
              }
              return {
                ...prev,
                [account.id]: {
                  balance: existingBalance || '—',
                  isLoading: true,
                },
              }
            })
          }
        })

        try {
          const publicClient = createPublicClient({
            chain,
            transport: http(rpcUrl),
          })

          // Get decimals and symbol once (they're the same for all accounts)
          const [decimals, symbol] = await Promise.all([
            publicClient.readContract({
              address: stngProxy as `0x${string}`,
              abi: STNGImplementationAbi as Abi,
              functionName: 'decimals',
            }) as Promise<number>,
            publicClient.readContract({
              address: stngProxy as `0x${string}`,
              abi: STNGImplementationAbi as Abi,
              functionName: 'symbol',
            }) as Promise<string>,
          ])

          const balanceCalls = chainAccounts.map((account) => ({
            address: stngProxy as `0x${string}`,
            abi: STNGImplementationAbi as Abi,
            functionName: 'balanceOf' as const,
            args: [account.address as `0x${string}`],
          }))

          const balances = await publicClient.multicall({
            contracts: balanceCalls,
          })

          balances.forEach((result, index) => {
            const account = chainAccounts[index]
            if (
              result.status === 'success' &&
              result.result !== undefined &&
              result.result !== null
            ) {
              const rawBalance = result.result as bigint
              const formattedBalance = formatTokenBalance(
                rawBalance,
                decimals,
                symbol
              )
              setBalances((prev) => {
                const currentBalance = prev[account.id]?.balance
                if (currentBalance === formattedBalance) {
                  return prev
                }
                return {
                  ...prev,
                  [account.id]: { balance: formattedBalance, isLoading: false },
                }
              })
            } else {
              setBalanceToError(account.id)
            }
          })
        } catch (error) {
          console.error(`Failed to fetch balances for chain ${chainId}:`, error)
          chainAccounts.forEach((account) => {
            setBalanceToError(account.id)
          })
        }
      }
    }

    fetchBalances()

    const interval = setInterval(fetchBalances, POLLING_INTERVAL_BALANCE)

    return () => {
      clearInterval(interval)
    }
  }, [accountsKey, stngProxy, mainnetRpcUrl, sepoliaRpcUrl, accounts])

  const handleEdit = (account: SafeAccount) => {
    setEditingId(account.id)
    setEditName(account.name)
  }

  const handleSave = async (id: string) => {
    const account = safes.find((acc) => acc.id === id)
    if (!account) {
      return
    }

    if (!apiUrl) {
      showError('API URL is not configured')
      return
    }

    setIsUpdatingName(id)
    try {
      await updateSafeName(apiUrl, account.safeId, editName.trim())
      setSafes((prev) =>
        prev.map((acc) =>
          acc.id === id ? { ...acc, name: editName.trim() } : acc
        )
      )
      showSuccess('Safe Account name updated')
      setEditingId(null)
      setEditName('')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update safe name'
      showError(errorMessage)
    } finally {
      setIsUpdatingName(null)
    }
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditName('')
  }

  const handleDelete = async (id: string) => {
    const account = safes.find((acc) => acc.id === id)
    if (
      !account ||
      !isTreasurySafeReady ||
      !operationalSafeFactory ||
      !chainId
    ) {
      showError('Cannot delete: missing required configuration')
      return
    }

    setIsDeleting(id)
    try {
      const data = encodeContractCall(
        OperationalSafeFactoryAbi as Abi,
        'removeOperationalSafe',
        [getAddress(account.address)]
      )

      const safeTx = await buildTreasurySafeTransaction([
        buildSafeTransactionParams({
          to: getAddress(operationalSafeFactory),
          data,
        }),
      ])

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsDeleting(null)
        return
      }

      let signedTx
      try {
        signedTx = await signTreasurySafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              treasurySafe,
              chainId,
              userAddress,
              getTreasurySafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsDeleting(null)
        if (isUserRejectedError(error)) {
          showError('You cancelled the signature request.')
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTreasuryTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeTreasurySafeTransaction(signedTx)
          if (hash) {
            deleteSafe(id)
            setSafes((prev) => prev.filter((acc) => acc.id !== id))
            showSuccess('Safe Account removed from registry')
          }
        } catch (error) {
          setIsDeleting(null)
          if (isUserRejectedError(error)) {
            showError('You cancelled the execution transaction.')
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute delete transaction')
          }
        }
      } else {
        setIsDeleting(null)
        getTreasurySafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Delete transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                txHash
              )}...`
            )
          })
          .catch(() => {
            showSuccess(
              'Delete transaction created and signed! It requires more signatures before execution.'
            )
          })
      }
    } catch (error) {
      setIsDeleting(null)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to delete Safe Account. Please try again.'
      )
    }
  }

  const handleBlock = async (id: string) => {
    const account = safes.find((acc) => acc.id === id)
    if (!account || !isTreasurySafeReady || !operationalSafeGuard || !chainId) {
      showError('Cannot block: missing required configuration')
      return
    }

    setIsBlocking(id)
    try {
      const data = encodeContractCall(SafeGuardAbi as Abi, 'pauseSafe', [
        getAddress(account.address),
      ])

      const safeTx = await buildTreasurySafeTransaction([
        buildSafeTransactionParams({
          to: operationalSafeGuard,
          data,
        }),
      ])

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsBlocking(null)
        return
      }

      let signedTx
      try {
        signedTx = await signTreasurySafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              treasurySafe,
              chainId,
              userAddress,
              getTreasurySafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsBlocking(null)
        if (isUserRejectedError(error)) {
          showError('You cancelled the signature request.')
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTreasuryTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeTreasurySafeTransaction(signedTx)
          if (hash) {
            blockSafe(id)
            showSuccess('Safe Account blocked')
          }
        } catch (error) {
          setIsBlocking(null)
          if (isUserRejectedError(error)) {
            showError('You cancelled the execution transaction.')
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute block transaction')
          }
        }
      } else {
        setIsBlocking(null)
        getTreasurySafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Block transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                txHash
              )}...`
            )
          })
          .catch(() => {
            showSuccess(
              'Block transaction created and signed! It requires more signatures before execution.'
            )
          })
      }
    } catch (error) {
      setIsBlocking(null)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to block Safe Account. Please try again.'
      )
    }
  }

  const handleUnblock = async (id: string) => {
    const account = safes.find((acc) => acc.id === id)
    if (!account || !isTreasurySafeReady || !operationalSafeGuard || !chainId) {
      showError('Cannot unblock: missing required configuration')
      return
    }

    setIsBlocking(id)
    try {
      const data = encodeContractCall(SafeGuardAbi as Abi, 'unpauseSafe', [
        getAddress(account.address),
      ])

      const safeTx = await buildTreasurySafeTransaction([
        buildSafeTransactionParams({
          to: operationalSafeGuard,
          data,
        }),
      ])

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsBlocking(null)
        return
      }

      let signedTx
      try {
        signedTx = await signTreasurySafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              treasurySafe,
              chainId,
              userAddress,
              getTreasurySafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsBlocking(null)
        if (isUserRejectedError(error)) {
          showError('You cancelled the signature request.')
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTreasuryTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeTreasurySafeTransaction(signedTx)
          if (hash) {
            unblockSafe(id)
            showSuccess('Safe Account unblocked')
          }
        } catch (error) {
          setIsBlocking(null)
          if (isUserRejectedError(error)) {
            showError('You cancelled the execution transaction.')
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute unblock transaction')
          }
        }
      } else {
        setIsBlocking(null)
        getTreasurySafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Unblock transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                txHash
              )}...`
            )
          })
          .catch(() => {
            showSuccess(
              'Unblock transaction created and signed! It requires more signatures before execution.'
            )
          })
      }
    } catch (error) {
      setIsBlocking(null)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to unblock Safe Account. Please try again.'
      )
    }
  }

  const handleCreate = () => {
    setIsCreateModalOpen(true)
  }

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false)
    loadSafes()
  }

  const handleRefresh = () => {
    loadSafes()
  }

  const handleCopyAddress = async (address: string) => {
    try {
      await copyToClipboard(address)
      showSuccess('Address copied to clipboard')
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const handleTransfer = (account: SafeAccount) => {
    setSelectedAccount(account)
    setTransferModalOpen(true)
  }

  const handleShowDetails = (account: SafeAccount) => {
    setSelectedDetailsAccount(account)
    setDetailsModalOpen(true)
  }

  const handleEditNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditName(e.target.value)
    },
    []
  )

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader
        title="Operational Safe Accounts"
        action={
          <Tooltip title="Refresh Safe Accounts">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isLoadingSafes}
                sx={{
                  color: colors.primary,
                  '&:hover': {
                    backgroundColor: `${colors.primary}22`,
                  },
                  '&:disabled': {
                    color: colors.text,
                    opacity: 0.5,
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: isLoadingSafes
                      ? 'spin 1s linear infinite'
                      : 'none',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <Box sx={createCardStyles(colors)}>
        {isLoadingSafes ? (
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: colors.primary }} />
            <Typography
              variant="body2"
              sx={{ ...createSecondaryTextStyles(colors) }}
            >
              Loading Safe Accounts...
            </Typography>
          </Box>
        ) : (
          <>
            <List>
              {accounts.map((account: SafeAccount) => {
                const isAccountDeleting = isDeleting === account.id
                const isAccountBlocking = isBlocking === account.id
                const isAccountPaused = pausedAccounts.has(account.id)
                const isAccountProcessing =
                  isAccountDeleting || isAccountBlocking
                const isAccountDisabled = isAccountPaused || isAccountProcessing

                return (
                  <ListItem
                    key={account.id}
                    sx={{
                      borderBottom: `1px solid ${colors.primary}33`,
                      py: 2,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 1 },
                        mb: 1,
                      }}
                    >
                      {editingId === account.id ? (
                        <>
                          <TextField
                            value={editName}
                            onChange={handleEditNameChange}
                            size="small"
                            sx={{
                              flex: 1,
                              mr: 1,
                              ...createTextFieldStyles(colors),
                            }}
                          />
                          <Button
                            onClick={() => handleSave(account.id)}
                            size="small"
                            disabled={isUpdatingName === account.id}
                            sx={{
                              color: colors.primary,
                              mr: 1,
                              borderColor: colors.primary,
                              border: '1px solid',
                            }}
                          >
                            {isUpdatingName === account.id ? (
                              <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Saving...
                              </>
                            ) : (
                              'Save'
                            )}
                          </Button>
                          <Button
                            onClick={handleCancel}
                            size="small"
                            sx={{
                              color: colors.text,
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flex: { xs: 'none', sm: 1 },
                              width: { xs: '100%', sm: 'auto' },
                              gap: 1,
                              minWidth: 0,
                              flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ color: colors.text }}
                            >
                              {account.name}
                            </Typography>
                            {isAccountPaused && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.error,
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                }}
                              >
                                Blocked
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: { xs: '100%', sm: 'auto' },
                              justifyContent: {
                                xs: 'flex-start',
                                sm: 'flex-end',
                              },
                              gap: { xs: 0.5, sm: 0 },
                              flexWrap: 'wrap',
                            }}
                          >
                            <Tooltip
                              title="transfer"
                              disableHoverListener={isAccountPaused}
                              disableFocusListener={isAccountPaused}
                            >
                              <span>
                                <IconButton
                                  onClick={() => handleTransfer(account)}
                                  disabled={isAccountDisabled}
                                  sx={{
                                    color: colors.text,
                                    mr: { xs: 0.5, sm: 1 },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <SwapHorizIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title="rename"
                              disableHoverListener={isAccountPaused}
                              disableFocusListener={isAccountPaused}
                            >
                              <span>
                                <IconButton
                                  onClick={() => handleEdit(account)}
                                  disabled={isAccountDisabled}
                                  sx={{
                                    color: colors.primary,
                                    mr: { xs: 0.5, sm: 1 },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title="delete"
                              disableHoverListener={
                                isAccountPaused || isAccountDeleting
                              }
                              disableFocusListener={
                                isAccountPaused || isAccountDeleting
                              }
                            >
                              <span>
                                <IconButton
                                  onClick={() => handleDelete(account.id)}
                                  disabled={isAccountDisabled}
                                  sx={{
                                    color: colors.purple,
                                    mr: { xs: 0.5, sm: 1 },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  {isAccountDeleting ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{ color: colors.purple }}
                                    />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            {isAccountPaused ? (
                              <Tooltip title="unblock">
                                <IconButton
                                  onClick={() => handleUnblock(account.id)}
                                  disabled={isAccountProcessing}
                                  sx={{ color: colors.primary }}
                                >
                                  {isAccountBlocking ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{ color: colors.primary }}
                                    />
                                  ) : (
                                    <LockOpenIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="block">
                                <IconButton
                                  onClick={() => handleBlock(account.id)}
                                  disabled={isAccountProcessing}
                                  sx={{ color: colors.yellow }}
                                >
                                  {isAccountBlocking ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{ color: colors.yellow }}
                                    />
                                  ) : (
                                    <BlockIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="details">
                              <span>
                                <IconButton
                                  onClick={() => handleShowDetails(account)}
                                  sx={{
                                    color: colors.primary,
                                    mr: { xs: 0.5, sm: 1 },
                                    '&:hover': {
                                      backgroundColor: `${colors.primary}22`,
                                    },
                                  }}
                                >
                                  <InfoIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={createSecondaryTextStyles(colors)}
                        >
                          {formatAddress(account.address)}
                        </Typography>
                        <Tooltip title="Copy address">
                          <IconButton
                            onClick={() => handleCopyAddress(account.address)}
                            size="small"
                            sx={{
                              color: colors.primary,
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: `${colors.primary}22`,
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Chip
                          label={
                            chains.find(
                              (c) => c.id.toString() === account.chainId
                            )?.name || account.chainId
                          }
                          size="small"
                          sx={{
                            backgroundColor: colors.primary + '33',
                            color: colors.text,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                      {balances[account.id]?.isLoading ? (
                        <Skeleton
                          variant="text"
                          width={SKELETON_WIDTH_SMALL}
                          sx={{ bgcolor: `${colors.primary}33` }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.text,
                            fontWeight: 500,
                            ml: { xs: 0, sm: 1 },
                          }}
                        >
                          Balance: {balances[account.id]?.balance || '—'}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                )
              })}
              {accounts.length === 0 && !isLoadingSafes && (
                <ListItem>
                  <Typography
                    variant="body2"
                    sx={{
                      ...createSecondaryTextStyles(colors),
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    No Safe Accounts found. Create your first Safe Account to
                    get started.
                  </Typography>
                </ListItem>
              )}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: colors.text,
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${colors.primary}33`,
                    color: colors.primary,
                  },
                }}
              />
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={handleCreate}
              variant="outlined"
              sx={{
                mt: 2,
                color: colors.primary,
                borderColor: colors.primary,
                border: '1px solid',
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}22`,
                },
              }}
            >
              Create New Safe Account
            </Button>
          </>
        )}
      </Box>
      <CreateSafeModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
      {selectedAccount && (
        <TransferModal
          open={transferModalOpen}
          onClose={() => {
            setTransferModalOpen(false)
            setSelectedAccount(null)
          }}
          safeAddress={selectedAccount.address}
          safeName={selectedAccount.name}
        />
      )}
      {selectedDetailsAccount && (
        <SafeDetailsModal
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false)
            setSelectedDetailsAccount(null)
          }}
          safeAddress={selectedDetailsAccount.address}
          safeName={selectedDetailsAccount.name}
          chainId={selectedDetailsAccount.chainId}
        />
      )}
    </Box>
  )
}
